<template>
    <v-container fluid>
        <v-card>
            <v-card-title primary-title>
                <v-row>
                    <v-col>Schema Configs</v-col>
                    <v-spacer />
                    <v-col class="text-right" v-if="userCanCreateProtocols">
                        <v-btn color="primary" @click="createSchemaConfig"
                            >Create Schema Config</v-btn
                        >
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-text>
                <v-data-table
                    :headers="schemaConfigHeaders"
                    :items="schemaConfigs"
                    item-key="id"
                    class="elevation-1"
                    :items-per-page="25"
                    :search="search"
                >
                    <template v-slot:item.edit="{ item }">
                        <v-row>
                            <v-icon @click="editSchemaConfig(item)">$pencil</v-icon>
                        </v-row>
                    </template>
                </v-data-table>
            </v-card-text>
            <v-dialog v-model="schemaConfigDialogVisible">
                <schema-config-form
                    @cancelForm="hideSchemaConfigForm"
                    @schemaConfigSaved="savedSchemaConfig"
                />
            </v-dialog>
        </v-card>
    </v-container>
</template>

<script setup lang="ts">
import { manageSchemasAPI, SavedSchemaConfigDTO } from "@/services/schemas/manageSchemasAPI";
import { withLoadingScreen } from "@/lib/loading";
import { computed, ref } from "vue";
import { useAuth } from "@/lib/ts/auth";
import { VDataTableHeader } from "@/vuetifyTypeFixes";
import router from "@/router";
import SchemaConfigForm from "@/components/SchemaConfig/SchemaConfigForm.vue";

const schemaConfigs = ref<SavedSchemaConfigDTO[]>([]);
const selectedSchemaConfig = ref<SavedSchemaConfigDTO>();
const schemaConfigDialogVisible = ref<boolean>(false);
const auth = useAuth();

interface Props {
    search: string | undefined;
}

withDefaults(defineProps<Props>(), {
    search: "",
});

const userCanCreateProtocols = computed(() => {
    return auth.isAuthorized("SchemaConfigs:Manage");
});

const schemaConfigHeaders = ref<VDataTableHeader[]>([
    {
        title: "Code",
        value: "code",
        sortable: true,
        align: "start",
        width: "10%",
    },
    {
        title: "Name",
        value: "name",
        sortable: true,
        align: "start",
        width: "85%",
    },
]);

if (userCanCreateProtocols.value) {
    schemaConfigHeaders.value.push(
        {
            title: "Edit",
            value: "edit",
            sortable: false,
            align: "start",
            width: "5%",
        }
    );
}

function showSchemaConfigForm() {
    schemaConfigDialogVisible.value = true;
}

function hideSchemaConfigForm() {
    schemaConfigDialogVisible.value = false;
    selectedSchemaConfig.value = undefined;
}

function createSchemaConfig() {
    selectedSchemaConfig.value = undefined;
    showSchemaConfigForm();
}

function editSchemaConfig(savedSchemaConfig: SavedSchemaConfigDTO) {
    router.push({
        name: "ViewSchemaConfig",
        params: {
            schemaConfigCode: savedSchemaConfig.code,
        },
    });
}

async function loadSchemaConfigs() {
    await withLoadingScreen(async () => {
        schemaConfigs.value = await manageSchemasAPI.fetchAllSchemaConfigs();
    });
}

async function savedSchemaConfig() {
    await withLoadingScreen(async () => {
        hideSchemaConfigForm();
        await loadSchemaConfigs();
    });
}

loadSchemaConfigs();
</script>
